import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';
import { getAttributionPayload, trackGoal, isTrackingEnabled, whenClientIdReady } from '@/lib/attribution';

// Resend ym_client_id to the backend if we have it now but it was missing
// from the original login/saveUser payload. Metrika's getClientID is async,
// so on slow init the CID arrives after we've already POSTed attribution.
function resendAttributionIfLate(hadCidBefore) {
  try {
    return whenClientIdReady(8000).then((cid) => {
      if (!cid || hadCidBefore) return;
      const authToken = localStorage.getItem('authToken');
      if (!authToken || authToken === 'undefined') return;
      axiosConfig.post(API_URL + '?action=update_attribution', {
        params: { attribution: { ym_client_id: cid } },
      }).catch(() => { /* late-arrival dosyl is best-effort */ });
    });
  } catch (e) { /* noop */ }
}

const userModule = {
  namespaced: true,
  state: {
    user: null,
    isAuthenticated: false,
    hasEditAccess: false
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setAuthentication(state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated;
    },
    setEditAccess(state, hasEditAccess) { // Add the setEditAccess mutation
      state.hasEditAccess = hasEditAccess;
    },    
  },
  actions: {
    updateAuthentication({ commit }, isAuthenticated) {
      commit('setAuthentication', isAuthenticated);
    },
    updateUser({ commit }, user) {
      commit('setUser', user);
    },

    async loginUser({ commit, dispatch, rootState }, payload) {
      // 2026-09-04: раньше сюда приходил ТОЛЬКО объект пользователя. Теперь Mini App
      // шлёт {user, initData}. Принимаем обе формы — виджетные вызовы не трогаем.
      const isWrapped = payload && typeof payload === 'object' && payload.user;
      const user = isWrapped ? payload.user : payload;
      const initData = isWrapped ? (payload.initData || '') : '';
      const configData = rootState.app?.configData || {};
      const chatbotName = configData.chatbot_name || configData.service_name || '';
      const metrikaId = configData.metrika_id;

      const params = { user };
      if (initData) params.init_data = initData;
      let hadCidBefore = false;
      if (isTrackingEnabled(chatbotName)) {
        try {
          // Wait briefly for Metrika's getClientID to populate localStorage.
          // 2s is enough on warm caches; if Metrika is blocked we fall through
          // and rely on resendAttributionIfLate() after login completes.
          await whenClientIdReady(2000);
          const attribution = getAttributionPayload();
          hadCidBefore = !!(attribution && attribution.ym_client_id);
          if (attribution && (attribution.first_touch || attribution.last_touch || attribution.ym_client_id)) {
            params.attribution = attribution;
          }
        } catch (e) { /* noop */ }
      }

      try {
        const response = await axiosConfig.post(API_URL + '?action=login', { params });
        if (response.data.authToken) {
          const authToken = response.data.authToken;
          localStorage.setItem('authToken', authToken); // Save the authToken in localStorage
          commit('setAuthentication', true);
          dispatch('checkUser'); // Call the checkUser action to update the user state

          // register goal — fires once per login success. Dedup happens in Metrika
          // by ClientID, so multiple hits from the same user are OK.
          if (isTrackingEnabled(chatbotName) && metrikaId) {
            trackGoal(metrikaId, 'register');
          }
          if (isTrackingEnabled(chatbotName)) resendAttributionIfLate(hadCidBefore);
        }
      } catch (error) {
        console.error('Login error:', error);
      }
    },


    logoutUser({ commit }) {
      localStorage.removeItem('authToken'); // Remove the authToken from localStorage
      commit('setUser', null);
      commit('setAuthentication', false);
    },

    async saveUser({ state, rootState }) {
      const configData = rootState.app?.configData || {};
      const chatbotName = configData.chatbot_name || configData.service_name || '';
      const metrikaId = configData.metrika_id;

      // Attribution payload — backend accepts it optionally; if tracking is
      // disabled or helpers fail, we just send {user} as before.
      const params = { user: state.user };
      let hadCidBefore = false;
      if (isTrackingEnabled(chatbotName)) {
        try {
          await whenClientIdReady(2000);
          const attribution = getAttributionPayload();
          hadCidBefore = !!(attribution && attribution.ym_client_id);
          if (attribution && (attribution.first_touch || attribution.last_touch || attribution.ym_client_id)) {
            params.attribution = attribution;
          }
        } catch (e) { /* noop */ }
      }

      try {
        const response = await axiosConfig.post(API_URL + '?action=save_user', { params });
        console.log(response);
        console.log('User data saved successfully');
        if (isTrackingEnabled(chatbotName) && metrikaId) {
          trackGoal(metrikaId, 'register');
        }
        if (isTrackingEnabled(chatbotName)) resendAttributionIfLate(hadCidBefore);
      } catch (error) {
        console.error('Error saving user data:', error);
      }
    },
    fetchUserAccess({ commit }) {
      axiosConfig
        .get(API_URL, {
          params: {
            action: 'get_user_access',
          },
        })
        .then(response => {
          const hasAccess = response.data.hasAccess;
          commit('setEditAccess', hasAccess); // Commit the hasEditAccess mutation
          return response.data;
        })
        .catch(error => {
          console.error('Error fetching user access:', error);
          throw error;
        });
    },    
    checkUser({ commit, dispatch, rootState }) {
      const authToken = localStorage.getItem('authToken');
      if (authToken && authToken !== 'undefined') {
        axiosConfig
          .get(API_URL + '?action=check_user', {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })
          .then(response => {
            commit('setUser', response.data.user);
            commit('setAuthentication', true);
            dispatch('fetchUserAccess');

            // Fetch subscription data after successful auth
            const configData = rootState.app?.configData || {};
            let chatbotName = configData.chatbot_name || configData.service_name || 'meetscribe';
            // Normalize clipcutglobal to clipcut for subscription API
            if (chatbotName === 'clipcutglobal') chatbotName = 'clipcut';
            dispatch('subscription/fetchSubscriptionStatus', chatbotName, { root: true });
          })
          .catch(error => {
            console.error('User check error:', error);
          });
      } else {
        commit('setUser', null);
        commit('setAuthentication', false);
      }
    },
  },
  getters: {
    getUser(state) {
      return state.user;
    },
    isAuthenticated(state) {
      return state.isAuthenticated;
    },
    hasEditAccess(state) {
      return state.hasEditAccess;
    },
  },
};

export default userModule;
